"use client";
import { motion } from "framer-motion";
import { Gauge, ListChecks, ScanText } from "lucide-react";

export function FeatureSection() {
  return (
    <section className="py-20 md:py-32 bg-muted/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header — same register as Tools section */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 md:mb-16"
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">
            How It Works
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
            Three things.<br />One upload.
          </h2>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

          {/* Grading */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-border bg-card p-6 flex flex-col min-h-[320px]"
          >
            <div className="flex items-start justify-between mb-6">
              <span className="text-[11px] font-mono text-muted-foreground">01</span>
              <Gauge className="w-4 h-4 text-muted-foreground/50" />
            </div>
            <h3 className="text-base font-semibold mb-2">Instant Grading</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Upload your work. Get a predicted grade in seconds — overall score plus a per-section breakdown.
            </p>

            {/* Mini grade ring */}
            <div className="mt-auto flex flex-col items-center pt-6 border-t border-border/50">
              <div className="relative w-16 h-16">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="3"
                    fill="none" className="text-border" />
                  <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="3"
                    fill="none" className="text-foreground/80" strokeDasharray="176"
                    strokeDashoffset="18" strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center text-xl font-bold text-foreground">
                  A
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground font-mono">92 / 100</p>
            </div>
          </motion.div>

          {/* Rubric */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-border bg-card p-6 flex flex-col min-h-[320px]"
          >
            <div className="flex items-start justify-between mb-6">
              <span className="text-[11px] font-mono text-muted-foreground">02</span>
              <ListChecks className="w-4 h-4 text-muted-foreground/50" />
            </div>
            <h3 className="text-base font-semibold mb-2">Rubric Matching</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Upload your professor's marking scheme. See exactly how you score on every criterion before you submit.
            </p>

            {/* Mini rubric bars */}
            <div className="mt-auto space-y-3 pt-6 border-t border-border/50">
              {[
                { label: "Thesis", pct: 100 },
                { label: "Analysis", pct: 83 },
                { label: "Structure", pct: 90 },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-[10px] font-mono text-muted-foreground w-14 shrink-0">
                    {r.label}
                  </span>
                  <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-foreground/60 rounded-full"
                      style={{ width: `${r.pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">
                    {r.pct}%
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Feedback */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.14 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-border bg-card p-6 flex flex-col min-h-[320px]"
          >
            <div className="flex items-start justify-between mb-6">
              <span className="text-[11px] font-mono text-muted-foreground">03</span>
              <ScanText className="w-4 h-4 text-muted-foreground/50" />
            </div>
            <h3 className="text-base font-semibold mb-2">Line-Level Feedback</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Not just a grade — flagged sentences, specific rewrites, and exactly what to fix before you submit.
            </p>

            {/* Mini text snippet */}
            <div className="mt-auto space-y-2.5 pt-6 border-t border-border/50">
              <div className="h-2 bg-muted rounded-full w-full" />
              <div className="h-2 bg-muted rounded-full w-4/5" />
              <div className="relative flex items-center gap-2">
                <div className="h-2 bg-amber-200 dark:bg-amber-800/50 rounded-full w-3/4" />
                <span className="text-[9px] font-mono text-muted-foreground border border-border bg-background px-1.5 py-0.5 rounded whitespace-nowrap">
                  vague →
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full w-5/6" />
              <div className="h-2 bg-muted rounded-full w-2/3" />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
